import { useMemo } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import {
  OwnerId,
  useAuth,
  useGetAllTeamsQuery,
  useGetRegionerQuery,
  type Region
} from 'prove-common'
import { fetchOrgSubtree } from '../delegation-explorer/fetchOrgSubtree'
import {
  collectOrgLeaves,
  mergeOrgLookups,
  type OrgLeaf
} from '../delegation-explorer/orgTree'
import { HIDDEN_DELEGATION_UNITS } from '../../config/hiddenDelegationUnits'
import { isDelegationPathHidden } from '../../utils/delegationVisibility'

type WorksheetOrg = {
  leaves: OrgLeaf[]
  isLoading: boolean
  error?: unknown
}

export const resolveWorksheetOrgError = (
  regionError: unknown,
  teamData: unknown,
  teamError: unknown,
  subtreeError: unknown
): unknown =>
  regionError ?? (teamData == null ? teamError : undefined) ?? subtreeError

export const useDelegationWorksheetOrg = (
  rootPath: OwnerId[],
  rootLabel?: string,
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): WorksheetOrg => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  const regioner = useGetRegionerQuery(rootPath.length === 0)
  const teams = useGetAllTeamsQuery()

  const roots = useMemo(() => {
    if (rootPath[0]?.type === 'region') {
      return [
        {
          ownerId: rootPath[0],
          name: rootLabel ?? rootPath[0].owner
        }
      ]
    }
    return (regioner.data ?? []).map((region: Region) => ({
      ownerId: new OwnerId('region', region.id),
      name: region.navn
    }))
  }, [regioner.data, rootLabel, rootPath])

  const subtrees = useQueries({
    queries: roots.map((root) => ({
      queryKey: [
        'delegationWorksheetOrg',
        root.ownerId.toString(),
        teams.dataUpdatedAt
      ],
      enabled:
        (rootPath.length > 0 || !regioner.isLoading) && teams.data != null,
      queryFn: () =>
        fetchOrgSubtree(queryClient, authFetch, root.ownerId, teams.data)
    }))
  })
  const lookup = useMemo(
    () =>
      mergeOrgLookups(
        subtrees.map((query) => query.data).filter((data) => data != null)
      ),
    [subtrees]
  )

  return {
    leaves: useMemo(
      () =>
        collectOrgLeaves(roots, lookup).filter(
          (leaf) => !isDelegationPathHidden(leaf.path, hiddenUnits)
        ),
      [lookup, roots, hiddenUnits]
    ),
    isLoading:
      regioner.isLoading ||
      teams.isLoading ||
      subtrees.some((query) => query.isLoading),
    error: resolveWorksheetOrgError(
      regioner.error,
      teams.data,
      teams.error,
      subtrees.find((query) => query.error != null)?.error
    )
  }
}
